/**
 * DrawService - Map drawing operations using mapbox-gl-draw
 *
 * Provides promise-based drawing methods for use with Nomad integration.
 * Supports point, line, and polygon drawing with cancellation and callbacks.
 *
 * @module services/drawing
 */

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type mapboxgl from 'mapbox-gl';
import { logger } from '../../utils/logger';

const log = logger('DrawService');

// Drawing modes from mapbox-gl-draw
// Note: We use string type for compatibility with mapbox-gl-draw's changeMode signature
type DrawModeType = string;

/**
 * Geometry change callback type
 */
export type GeometryChangeCallback = (geometry: GeoJSON.Geometry | null) => void;

/**
 * DrawService class for managing map drawing operations
 */
// Layer IDs for the static preview (shown after drawing completes)
const PREVIEW_SOURCE_ID = 'draw-preview-source';
const PREVIEW_FILL_LAYER_ID = 'draw-preview-fill';
const PREVIEW_LINE_LAYER_ID = 'draw-preview-line';
const PREVIEW_POINT_LAYER_ID = 'draw-preview-point';

export class DrawService {
  private map: mapboxgl.Map;
  private draw: MapboxDraw;
  private currentResolve: ((geometry: GeoJSON.Geometry) => void) | null = null;
  private currentReject: ((error: Error) => void) | null = null;
  private geometryChangeCallbacks: Set<GeometryChangeCallback> = new Set();
  private isDrawing = false;
  private hasPreviewLayer = false;

  constructor(map: mapboxgl.Map) {
    this.map = map;

    // Initialize mapbox-gl-draw
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {}, // No UI controls - we manage programmatically
      defaultMode: 'simple_select',
      styles: this.getDrawStyles(),
    });

    // Add draw control to map
    this.map.addControl(this.draw as unknown as mapboxgl.IControl);

    // Listen for draw events
    this.map.on('draw.create', this.handleDrawCreate);
    this.map.on('draw.update', this.handleDrawUpdate);
    this.map.on('draw.render', this.handleDrawRender);

    log.debug('DrawService initialized');
  }

  /**
   * Get custom drawing styles
   */
  private getDrawStyles(): object[] {
    return [
      // Polygon fill
      {
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        paint: {
          'fill-color': '#FF6B00',
          'fill-outline-color': '#FF6B00',
          'fill-opacity': 0.2,
        },
      },
      // Polygon stroke (active)
      {
        id: 'gl-draw-polygon-stroke-active',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#FF6B00',
          'line-dasharray': [0.2, 2],
          'line-width': 3,
        },
      },
      // Line string
      {
        id: 'gl-draw-line',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#FF6B00',
          'line-dasharray': [0.2, 2],
          'line-width': 3,
        },
      },
      // Vertex points
      {
        id: 'gl-draw-polygon-and-line-vertex-active',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 6,
          'circle-color': '#FFF',
          'circle-stroke-color': '#FF6B00',
          'circle-stroke-width': 2,
        },
      },
      // Point
      {
        id: 'gl-draw-point',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
        paint: {
          'circle-radius': 8,
          'circle-color': '#FF6B00',
          'circle-stroke-color': '#FFF',
          'circle-stroke-width': 2,
        },
      },
      // Midpoint
      {
        id: 'gl-draw-polygon-midpoint',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
        paint: {
          'circle-radius': 4,
          'circle-color': '#FF6B00',
        },
      },
    ];
  }

  /**
   * Handle draw.create event - drawing completed
   */
  private handleDrawCreate = (e: { features: GeoJSON.Feature[] }) => {
    log.debug('Draw create event', { featureCount: e.features.length });

    if (e.features.length > 0 && this.currentResolve) {
      const geometry = e.features[0].geometry;
      this.completeDrawing(geometry);
    }
  };

  /**
   * Handle draw.update event
   */
  private handleDrawUpdate = (e: { features: GeoJSON.Feature[] }) => {
    if (e.features.length > 0) {
      this.notifyGeometryChange(e.features[0].geometry);
    }
  };

  /**
   * Handle draw.render event - called during drawing
   */
  private handleDrawRender = () => {
    // Get current drawing features for live updates
    const features = this.draw.getAll();
    if (features.features.length > 0 && this.isDrawing) {
      this.notifyGeometryChange(features.features[0].geometry);
    }
  };

  /**
   * Complete the current drawing operation
   * Replaces the editable mapbox-gl-draw features with a static GeoJSON preview layer
   */
  private completeDrawing(geometry: GeoJSON.Geometry) {
    if (this.currentResolve) {
      const resolve = this.currentResolve;
      this.isDrawing = false;
      this.currentResolve = null;
      this.currentReject = null;

      // Clear the editable mapbox-gl-draw features
      this.draw.deleteAll();
      this.draw.changeMode('simple_select');

      // Reset cursor
      this.map.getCanvas().style.cursor = '';

      // Add a static preview layer (non-editable)
      this.addPreviewLayer(geometry);

      resolve(geometry);
    }
  }

  /**
   * Add a static preview layer showing the completed geometry
   */
  private addPreviewLayer(geometry: GeoJSON.Geometry): void {
    // Remove any existing preview layer first
    this.removePreviewLayer();

    // Add the source
    this.map.addSource(PREVIEW_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry,
        properties: {},
      } as GeoJSON.Feature,
    });

    // Add appropriate layer based on geometry type
    if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
      this.map.addLayer({
        id: PREVIEW_POINT_LAYER_ID,
        type: 'circle',
        source: PREVIEW_SOURCE_ID,
        paint: {
          'circle-radius': 8,
          'circle-color': '#FF6B00',
          'circle-stroke-color': '#FFF',
          'circle-stroke-width': 2,
        },
      });
    } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
      this.map.addLayer({
        id: PREVIEW_LINE_LAYER_ID,
        type: 'line',
        source: PREVIEW_SOURCE_ID,
        paint: {
          'line-color': '#FF6B00',
          'line-width': 3,
        },
      });
    } else if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
      // Add fill layer
      this.map.addLayer({
        id: PREVIEW_FILL_LAYER_ID,
        type: 'fill',
        source: PREVIEW_SOURCE_ID,
        paint: {
          'fill-color': '#FF6B00',
          'fill-opacity': 0.3,
        },
      });
      // Add outline layer
      this.map.addLayer({
        id: PREVIEW_LINE_LAYER_ID,
        type: 'line',
        source: PREVIEW_SOURCE_ID,
        paint: {
          'line-color': '#FF6B00',
          'line-width': 3,
        },
      });
    }

    this.hasPreviewLayer = true;
    log.debug('Added preview layer for completed geometry', { type: geometry.type });
  }

  /**
   * Remove the static preview layer if it exists
   */
  private removePreviewLayer(): void {
    if (!this.hasPreviewLayer) return;

    // Remove layers (must remove before source)
    if (this.map.getLayer(PREVIEW_POINT_LAYER_ID)) {
      this.map.removeLayer(PREVIEW_POINT_LAYER_ID);
    }
    if (this.map.getLayer(PREVIEW_LINE_LAYER_ID)) {
      this.map.removeLayer(PREVIEW_LINE_LAYER_ID);
    }
    if (this.map.getLayer(PREVIEW_FILL_LAYER_ID)) {
      this.map.removeLayer(PREVIEW_FILL_LAYER_ID);
    }
    // Remove source
    if (this.map.getSource(PREVIEW_SOURCE_ID)) {
      this.map.removeSource(PREVIEW_SOURCE_ID);
    }

    this.hasPreviewLayer = false;
    log.debug('Removed preview layer');
  }

  /**
   * Notify all geometry change callbacks
   */
  private notifyGeometryChange(geometry: GeoJSON.Geometry | null) {
    this.geometryChangeCallbacks.forEach((callback) => {
      try {
        callback(geometry);
      } catch (err) {
        log.error('Error in geometry change callback', err);
      }
    });
  }

  /**
   * Start a drawing operation
   */
  private startDrawing(mode: DrawModeType): Promise<GeoJSON.Geometry> {
    // Cancel any existing drawing BEFORE creating the new promise
    // This ensures the old promise is rejected with the old reject function
    if (this.isDrawing) {
      log.debug('Cancelling previous drawing before starting new one');
      this.cancelDraw();
    }

    // Remove any existing preview layer from a previous drawing
    this.removePreviewLayer();

    return new Promise((resolve, reject) => {
      this.isDrawing = true;
      this.currentResolve = resolve;
      this.currentReject = reject;

      // Clear any existing features
      this.draw.deleteAll();

      // Set cursor to crosshair
      this.map.getCanvas().style.cursor = 'crosshair';

      // Change to the requested draw mode
      this.draw.changeMode(mode);

      log.debug('Started drawing', { mode });
    });
  }

  /**
   * Draw a point on the map
   * @returns Promise that resolves with the drawn point geometry
   */
  drawPoint(): Promise<GeoJSON.Point> {
    return this.startDrawing('draw_point') as Promise<GeoJSON.Point>;
  }

  /**
   * Draw a line on the map
   * @returns Promise that resolves with the drawn line geometry
   */
  drawLine(): Promise<GeoJSON.LineString> {
    return this.startDrawing('draw_line_string') as Promise<GeoJSON.LineString>;
  }

  /**
   * Draw a polygon on the map
   * @returns Promise that resolves with the drawn polygon geometry
   */
  drawPolygon(): Promise<GeoJSON.Polygon> {
    return this.startDrawing('draw_polygon') as Promise<GeoJSON.Polygon>;
  }

  /**
   * Subscribe to geometry changes during drawing
   * @param callback - Called with the current geometry state
   * @returns Unsubscribe function
   */
  onGeometryChange(callback: GeometryChangeCallback): () => void {
    this.geometryChangeCallbacks.add(callback);
    return () => {
      this.geometryChangeCallbacks.delete(callback);
    };
  }

  /**
   * Cancel the current drawing operation
   */
  cancelDraw(): void {
    if (this.isDrawing) {
      log.debug('Cancelling draw operation');

      const reject = this.currentReject;

      this.isDrawing = false;
      this.currentResolve = null;
      this.currentReject = null;

      // Clear drawn features
      this.draw.deleteAll();
      this.draw.changeMode('simple_select');

      // Reset cursor
      this.map.getCanvas().style.cursor = '';

      // Notify callbacks that drawing was cancelled
      this.notifyGeometryChange(null);

      // Reject the promise
      if (reject) {
        reject(new Error('Drawing cancelled'));
      }
    }
  }

  /**
   * Check if currently drawing
   */
  get drawing(): boolean {
    return this.isDrawing;
  }

  /**
   * Clear all drawn features from the map
   * Call this when the wizard completes or is cancelled
   */
  clearDrawnFeatures(): void {
    log.debug('Clearing drawn features');
    this.draw.deleteAll();
    this.removePreviewLayer();
    this.notifyGeometryChange(null);
  }

  /**
   * Dispose of the DrawService and clean up
   */
  dispose(): void {
    log.debug('Disposing DrawService');

    // Remove event listeners
    this.map.off('draw.create', this.handleDrawCreate);
    this.map.off('draw.update', this.handleDrawUpdate);
    this.map.off('draw.render', this.handleDrawRender);

    // Remove draw control
    this.map.removeControl(this.draw as unknown as mapboxgl.IControl);

    // Clear callbacks
    this.geometryChangeCallbacks.clear();

    // Cancel any active drawing
    if (this.isDrawing) {
      this.cancelDraw();
    }
  }
}

// Singleton instance management
let drawServiceInstance: DrawService | null = null;

/**
 * Get or create the DrawService instance for a map
 * @param map - The mapbox-gl map instance
 */
export function getDrawService(map: mapboxgl.Map): DrawService {
  if (!drawServiceInstance) {
    drawServiceInstance = new DrawService(map);
  }
  return drawServiceInstance;
}

/**
 * Dispose of the current DrawService instance
 */
export function disposeDrawService(): void {
  if (drawServiceInstance) {
    drawServiceInstance.dispose();
    drawServiceInstance = null;
  }
}
